import React, { useState, useEffect, useCallback } from "react";
import { Download } from "lucide-react";
import { jsPDF } from "jspdf";
import { useSiteContext } from "../../../../context/SiteContext";
import { useWhiteLabel } from "../../../../context/WhiteLabelContext";
import requests from "../../../../services/httpServices";

export default function MemberBilling() {
  const { user } = useSiteContext();
  const { branding } = useWhiteLabel();
  const [billing, setBilling] = useState(null);
  const [toast, setToast] = useState("");
  const memberId = user?.memberId;

  useEffect(() => {
    if (!memberId) return;
    let cancelled = false;
    const fetchBilling = async () => {
      try {
        const data = await requests.get(`/member/${memberId}/billing`);
        if (cancelled) return;
        if (data?.success && data?.data) {
          const d = data.data;
          setBilling({
            plan: d.plan ? d.plan.charAt(0).toUpperCase() + d.plan.slice(1) : "Basic",
            monthlyFee: d.monthlyFee || 0,
            nextBilling: d.nextBilling || "N/A",
            cardLast4: d.paymentMethod?.last4 || null,
            cardBrand: d.paymentMethod?.brand || null,
            cardExpMonth: d.paymentMethod?.exp_month,
            cardExpYear: d.paymentMethod?.exp_year,
            invoices: d.invoices || [],
          });
        } else {
          setBilling({ plan: user?.plan || "Basic", monthlyFee: 0, nextBilling: "N/A", cardLast4: null, cardBrand: null, invoices: [] });
        }
      } catch {
        if (!cancelled) setBilling({ plan: user?.plan || "Basic", monthlyFee: 0, nextBilling: "N/A", cardLast4: null, cardBrand: null, invoices: [] });
      }
    };
    fetchBilling();
    return () => { cancelled = true; };
  }, [memberId, user?.plan]);

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(""), 2000);
  };

  const openBillingPortal = useCallback(async () => {
    if (!memberId) return;
    try {
      const data = await requests.post(`/member/${memberId}/billing-portal`, { returnUrl: window.location.href });
      if (data?.success && data?.url) {
        window.location.href = data.url;
      } else {
        showToast(data?.message || "Unable to open billing portal.");
      }
    } catch {
      showToast("Failed to open billing portal.");
    }
  }, [memberId]);

  const generateInvoicePDF = (invoice) => {
    const doc = new jsPDF();
    const companyName = branding?.companyName || "R-Link Studio";
    const memberName = user?.name || "";
    const memberEmail = user?.email || "";
    const plan = billing?.plan || "Basic";

    doc.setFillColor(0, 18, 51);
    doc.rect(0, 0, 210, 40, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text(companyName, 20, 25);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("INVOICE", 170, 18);
    doc.text(String(invoice.id || ""), 170, 25);

    doc.setTextColor(0, 18, 51);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("BILL TO", 20, 55);
    doc.setFont("helvetica", "normal");
    doc.text(memberName, 20, 62);
    doc.text(memberEmail, 20, 68);

    doc.setFont("helvetica", "bold");
    doc.text("INVOICE DATE", 130, 55);
    doc.setFont("helvetica", "normal");
    doc.text(String(invoice.date || ""), 130, 62);
    doc.setFont("helvetica", "bold");
    doc.text("STATUS", 130, 72);
    doc.setFont("helvetica", "normal");
    doc.text(String(invoice.status || ""), 130, 79);

    doc.setDrawColor(200, 200, 200);
    doc.line(20, 90, 190, 90);

    doc.setFillColor(245, 245, 250);
    doc.rect(20, 95, 170, 10, "F");
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(100, 100, 120);
    doc.text("DESCRIPTION", 25, 101);
    doc.text("PLAN", 100, 101);
    doc.text("AMOUNT", 160, 101);

    doc.setTextColor(0, 18, 51);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("Monthly Subscription", 25, 115);
    doc.text(`${plan} Plan`, 100, 115);
    doc.setFont("helvetica", "bold");
    doc.text(`$${invoice.amount}.00`, 160, 115);

    doc.line(20, 125, 190, 125);
    doc.setFontSize(12);
    doc.text("Total:", 130, 135);
    doc.setTextColor(0, 200, 83);
    doc.text(`$${invoice.amount}.00`, 160, 135);

    doc.setTextColor(150, 150, 160);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("Thank you for your business. Payment processed via Stripe.", 20, 270);
    doc.text(`Powered by ${companyName}`, 20, 276);

    return doc;
  };

  const handleDownloadInvoice = (invoice) => {
    if (invoice.invoicePdf) { window.open(invoice.invoicePdf, "_blank"); showToast(`Opening invoice ${invoice.id}`); return; }
    if (invoice.hostedInvoiceUrl) { window.open(invoice.hostedInvoiceUrl, "_blank"); showToast(`Opening invoice ${invoice.id}`); return; }
    const doc = generateInvoicePDF(invoice);
    doc.save(`${invoice.id}.pdf`);
    showToast(`Downloaded ${invoice.id}.pdf`);
  };

  if (!memberId) return <div style={{ padding: 40, color: "rgba(255,255,255,0.4)" }}>Member account required.</div>;
  if (!billing) return <div style={{ padding: 40, color: "rgba(255,255,255,0.4)" }}>Loading...</div>;

  const hasOverdue = billing.invoices?.some((inv) => inv.status === "Overdue");

  return (
    <div style={{ padding: "8px 16px 40px", fontFamily: "'DM Sans', sans-serif", color: "#ffffff" }}>
      {hasOverdue && (
        <div style={{ padding: "16px 20px", borderRadius: 12, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", color: "#ef4444", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32, fontSize: 14 }}>
          <span>You have an overdue invoice. Please update your payment method to avoid service interruption.</span>
        </div>
      )}

      <div style={{ marginBottom: 32 }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)", marginBottom: 8 }}>BILLING</div>
        <h1 style={{ fontSize: 30, fontWeight: 800, color: "#ffffff", margin: "0 0 8px 0" }}>Billing & Invoices</h1>
        <p style={{ fontSize: 15, color: "rgba(255,255,255,0.6)", margin: 0 }}>Manage your subscription and download invoices.</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20, marginBottom: 32 }}>
        <div style={card}>
          <div style={sectionLabel}>CURRENT PLAN</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#ffffff", marginBottom: 8 }}>{billing.plan} Plan</div>
          <div style={{ display: "inline-block", padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: billing.plan === "Business" ? "rgba(106,31,191,0.2)" : "rgba(0,200,83,0.2)", color: billing.plan === "Business" ? "#a64dff" : "#00c853", marginBottom: 16 }}>{billing.plan}</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#00c853", marginBottom: 16 }}>${billing.monthlyFee}/mo</div>
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 16, marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginBottom: 4 }}>Next billing date:</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#ffffff" }}>{billing.nextBilling}</div>
          </div>
          <a href="#" onClick={(e) => { e.preventDefault(); openBillingPortal(); }} style={{ display: "inline-block", marginTop: 12, fontSize: 14, color: "#6a1fbf", textDecoration: "none", fontWeight: 600, cursor: "pointer" }}>
            Manage Subscription →
          </a>
        </div>

        <div style={card}>
          <div style={sectionLabel}>PAYMENT METHOD</div>
          {billing.cardLast4 ? (
            <>
              <div style={{ display: "inline-block", padding: "4px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.7)", marginBottom: 12, textTransform: "uppercase" }}>{billing.cardBrand || "CARD"}</div>
              <div style={{ fontSize: 18, fontWeight: 600, color: "#ffffff", marginBottom: 4, letterSpacing: "0.05em" }}>•••• •••• •••• {billing.cardLast4}</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginBottom: 2 }}>{billing.cardBrand} ending in {billing.cardLast4}</div>
              {billing.cardExpMonth && billing.cardExpYear && (
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginBottom: 16 }}>
                  Expires {String(billing.cardExpMonth).padStart(2, "0")}/{String(billing.cardExpYear).slice(-2)}
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: 14, color: "rgba(255,255,255,0.4)", marginBottom: 16 }}>No payment method on file.</div>
          )}
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 16, marginTop: 16 }} />
          <a href="#" onClick={(e) => { e.preventDefault(); openBillingPortal(); }} style={{ display: "inline-block", marginTop: 12, fontSize: 14, color: "#6a1fbf", textDecoration: "none", fontWeight: 600, cursor: "pointer" }}>
            Update Payment Method →
          </a>
        </div>
      </div>

      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: "#ffffff", margin: 0 }}>Invoice History</h3>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                {["INVOICE", "DATE", "AMOUNT", "STATUS", "ACTION"].map((h) => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(billing.invoices || []).length === 0 && (
                <tr><td colSpan={5} style={{ padding: "24px 16px", fontSize: 14, color: "rgba(255,255,255,0.4)", textAlign: "center" }}>No invoices yet.</td></tr>
              )}
              {billing.invoices?.map((invoice) => (
                <tr key={invoice.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)", background: invoice.status === "Overdue" ? "rgba(239,68,68,0.03)" : "transparent" }}>
                  <td style={{ padding: "14px 16px", fontSize: 14, fontWeight: 600, color: "#ffffff", fontFamily: "monospace" }}>{invoice.id}</td>
                  <td style={{ padding: "14px 16px", fontSize: 14, color: "rgba(255,255,255,0.7)" }}>{invoice.date}</td>
                  <td style={{ padding: "14px 16px", fontSize: 14, fontWeight: 600, color: "#00c853" }}>${invoice.amount}</td>
                  <td style={{ padding: "14px 16px" }}>
                    <span style={{ display: "inline-block", padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                      background: invoice.status === "Paid" ? "rgba(0,200,83,0.15)" : "rgba(239,68,68,0.15)",
                      color: invoice.status === "Paid" ? "#00c853" : "#ef4444" }}>
                      {invoice.status}
                    </span>
                  </td>
                  <td style={{ padding: "14px 16px" }}>
                    <button onClick={() => handleDownloadInvoice(invoice)}
                      style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "none", color: "rgba(255,255,255,0.5)", cursor: "pointer", fontSize: 13 }}>
                      <Download size={14} /> Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {toast && (
        <div style={{ position: "fixed", bottom: 20, right: 20, padding: "12px 20px", borderRadius: 8, background: "#0d1b3e", border: "1px solid rgba(255,255,255,0.1)", color: "#ffffff", fontSize: 14, boxShadow: "0 8px 24px rgba(0,0,0,0.15)", zIndex: 200 }}>
          {toast}
        </div>
      )}
    </div>
  );
}

const card = {
  padding: 24,
  borderRadius: 14,
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.08)",
};

const sectionLabel = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.4)",
  marginBottom: 12,
};
